import { supabase } from '../../supabase';
import {
  PARTIDAS_EXPORT_PAGE_SIZE,
  PARTIDAS_PAGE_SIZE,
} from '../../constants/partidas';

const nullableArray = (values) => (values?.length ? values : null);
const nullableText = (value) => (String(value || '').trim() ? String(value).trim() : null);

const rpcFilters = (filters) => ({
  p_busqueda: nullableText(filters.busqueda),
  p_productos: nullableArray(filters.productos),
  p_niveles: nullableArray(filters.niveles)?.map(Number) || null,
  p_proveedores: nullableArray(filters.proveedores),
  p_estados: nullableArray(filters.estados),
  p_desde: filters.fechaDesde || null,
  p_hasta: filters.fechaHasta || null,
});

const applyViewFilters = (query, filters) => {
  let next = query;
  const search = nullableText(filters.busqueda)?.replace(/[,%()]/g, ' ');

  if (search) {
    next = next.or(
      `codigo_articulo.ilike.%${search}%,detalle.ilike.%${search}%,numero_partida.ilike.%${search}%,proveedor.ilike.%${search}%`
    );
  }
  if (filters.productos?.length) next = next.in('detalle', filters.productos);
  if (filters.niveles?.length) next = next.in('nivel1', filters.niveles.map(Number));
  if (filters.proveedores?.length) next = next.in('proveedor', filters.proveedores);
  if (filters.estados?.length) next = next.in('estado_vencimiento', filters.estados);
  if (filters.fechaDesde) next = next.gte('fecha_vencimiento', filters.fechaDesde);
  if (filters.fechaHasta) next = next.lte('fecha_vencimiento', filters.fechaHasta);

  return next;
};

const throwIfError = (error) => {
  if (!error) return;
  const setupHint = /does not exist|schema cache|Could not find/i.test(error.message || '')
    ? ' Verificá que la vista y las funciones RPC de Partidas estén instaladas en el proyecto Supabase configurado.'
    : '';
  throw new Error(`${error.message || 'Error de Supabase.'}${setupHint}`);
};

export const getPartidas = async ({
  filters,
  page = 1,
  pageSize = PARTIDAS_PAGE_SIZE,
  sortField = 'fecha_vencimiento',
  sortAscending = true,
}) => {
  let query = supabase
    .from('v_partidas_vencimiento')
    .select('*', { count: 'exact' });
  query = applyViewFilters(query, filters)
    .order(sortField, { ascending: sortAscending, nullsFirst: false })
    .order('id', { ascending: true })
    .range((page - 1) * pageSize, page * pageSize - 1);

  const { data, error, count } = await query;
  throwIfError(error);
  return { rows: data || [], total: count || 0 };
};

export const getPartidasResumen = async (filters) => {
  const { data, error } = await supabase.rpc('partidas_resumen', rpcFilters(filters));
  throwIfError(error);
  return data || {};
};

export const getPartidasFilterOptions = async () => {
  const { data, error } = await supabase.rpc('partidas_filtros');
  throwIfError(error);
  return {
    productos: data?.productos || [],
    niveles: data?.niveles || [],
    proveedores: data?.proveedores || [],
  };
};

export const getPartidasAuditoria = async (filters) => {
  const { data, error } = await supabase.rpc('partidas_auditoria', {
    ...rpcFilters(filters),
    p_limite: 2000,
  });
  throwIfError(error);
  return data || [];
};

export const getAllPartidasForExport = async (filters, {
  sortField = 'fecha_vencimiento',
  sortAscending = true,
} = {}) => {
  const allRows = [];
  let page = 0;
  let keepLoading = true;

  while (keepLoading) {
    let query = supabase.from('v_partidas_vencimiento').select('*');
    query = applyViewFilters(query, filters)
      .order(sortField, { ascending: sortAscending, nullsFirst: false })
      .order('id', { ascending: true })
      .range(
        page * PARTIDAS_EXPORT_PAGE_SIZE,
        (page + 1) * PARTIDAS_EXPORT_PAGE_SIZE - 1
      );

    const { data, error } = await query;
    throwIfError(error);
    const rows = data || [];
    allRows.push(...rows);
    keepLoading = rows.length === PARTIDAS_EXPORT_PAGE_SIZE;
    page += 1;
  }

  return allRows;
};

export const importPartidasSnapshot = async (analysis, snapshotCompleto = true) => {
  const { data, error } = await supabase.rpc('partidas_importar_snapshot', {
    p_tipo: analysis.type,
    p_archivo_nombre: analysis.fileName,
    p_archivo_sha256: analysis.fileHash,
    p_archivo_bytes: analysis.fileSize,
    p_filas: analysis.rows,
    p_snapshot_completo: snapshotCompleto,
  });
  throwIfError(error);
  if (data?.ok === false) throw new Error(data.error || 'La importación no pudo completarse.');
  return data;
};

export const getImportHistory = async (limit = 50) => {
  const { data, error } = await supabase
    .from('partida_importaciones')
    .select('*')
    .order('iniciado_at', { ascending: false })
    .limit(limit);
  throwIfError(error);
  return data || [];
};

export const getImportIssues = async (importId) => {
  const { data, error } = await supabase
    .from('partida_importacion_errores')
    .select('*')
    .eq('importacion_id', importId)
    .order('severidad', { ascending: false })
    .order('numero_fila', { ascending: true });
  throwIfError(error);
  return data || [];
};

export const getLastPartidasImport = async () => {
  const { data, error } = await supabase
    .from('partida_importaciones')
    .select('id, archivo_nombre, finalizado_at, estado, filas_leidas')
    .eq('tipo_archivo', 'partidas_activas')
    .in('estado', ['completada', 'completada_con_observaciones'])
    .order('finalizado_at', { ascending: false })
    .limit(1)
    .maybeSingle();
  throwIfError(error);
  return data || null;
};
